/**
 * What the board asks the producer to do, and what it does with the answer.
 *
 * None of this touches React, so the whole of it is tested with a fake api in
 * `test/board-state.test.ts`: the version an operation is sent with, the board it
 * comes back with, where the card it wrote belongs, and what the host is told
 * when the producer refuses.
 */
import type {
  AutomationDetail,
  BoardCard,
  BoardCardRequest,
  BoardCardResponse,
  BoardLane,
  BoardState,
  BoardStateResponse,
  SaveGraphRequest,
  UpdateAutomationRequest,
} from "../../shared/types";
import { VIDEO_READY } from "../../shared/config";
import { ApiError } from "../lib/api";
import type { Editor } from "../components/FlowEditor";
import { treeToGraph } from "../automations/flow-model";
import type { BoardCandidate } from "./host";
import { laneOf } from "./state";

interface BoardOpsApi {
  card(id: string, body: BoardCardRequest): Promise<BoardCardResponse>;
  get(id: string): Promise<BoardStateResponse>;
}

/** What one producer operation came to. */
type OpOutcome =
  | {
      kind: "done";
      state: BoardState;
      version: number;
      card?: BoardCard;
      candidates?: BoardCandidate[];
    }
  /** The board moved on under us: this is the server's, and nothing of ours is replayed. */
  | { kind: "reloaded"; server: BoardStateResponse }
  /** The producer would not do it. `error` is its own line, for the card that asked. */
  | { kind: "refused"; error: string };

const GENERIC = "AI Studio could not do that.";

/**
 * One operation, sent on the version the queue is holding. A 409 means somebody
 * else saved while the model was thinking, so the server's board comes back
 * instead of ours; anything else comes back as the server's own refusal.
 */
export async function runCardOp(
  api: BoardOpsApi,
  webinarId: string,
  version: number,
  body: Omit<BoardCardRequest, "version">,
): Promise<OpOutcome> {
  try {
    const res = await api.card(webinarId, { ...body, version });
    return {
      kind: "done",
      state: res.state,
      version: res.version,
      ...(res.card ? { card: res.card } : {}),
      ...(res.candidates ? { candidates: res.candidates } : {}),
    };
  } catch (error) {
    if (error instanceof ApiError && (error.status === 409 || error.status === 404)) {
      const server = await api.get(webinarId).catch(() => null);
      if (server) return { kind: "reloaded", server };
      return { kind: "refused", error: error.message };
    }
    return { kind: "refused", error: error instanceof ApiError ? error.message : GENERIC };
  }
}

/** The lane a card the producer just wrote lives in, so the board can show it. */
export function laneForCard(state: BoardState, card: BoardCard | undefined): BoardLane | null {
  return card ? laneOf(state, card.id) : null;
}

/**
 * Bring a card in the left column into view. The preview stamps the same
 * attribute on its own sections, so the lane is named: it is the card the host
 * edits that has to be on screen, not the paragraph it draws.
 */
export function showCard(cardId: string): void {
  if (typeof document === "undefined") return;
  const el = document.querySelector(`.cards [data-card-id="${cardId}"]`);
  el?.scrollIntoView({ block: "nearest" });
}

// ── the transcript, when the recording's own read did not happen ─────────
/**
 * The video Workflow transcribes a board's recording on its way through, so on
 * the happy path the transcript is there before the video is playable. When that
 * best-effort step failed, the recording is ready and there is nothing to read:
 * the board asks for the transcript itself, once.
 */
export function needsTranscribe(board: BoardStateResponse | null, asked: boolean): boolean {
  if (!board || asked) return false;
  return board.video.state === VIDEO_READY && !board.transcriptPresent && !!board.video.videoId;
}

interface TranscribeApi {
  transcribe(webinarId: string, body: { videoId: string }): Promise<unknown>;
}

/** Ask for the transcript. The refusal, when there is one, is the server's own. */
export async function runTranscribe(
  api: TranscribeApi,
  webinarId: string,
  videoId: string,
): Promise<{ ok: boolean; error: string }> {
  try {
    await api.transcribe(webinarId, { videoId });
    return { ok: true, error: "" };
  } catch (error) {
    return {
      ok: false,
      error:
        error instanceof ApiError ? error.message : "We could not listen to that talk.",
    };
  }
}

// ── follow-ups are real automations ──────────────────────────────────────
interface FollowUpApi {
  get(id: string): Promise<AutomationDetail>;
  saveGraph(id: string, body: SaveGraphRequest, init?: RequestInit): Promise<unknown>;
  update(id: string, body: UpdateAutomationRequest, init?: RequestInit): Promise<AutomationDetail>;
}

interface FollowUpWrite {
  detail: AutomationDetail | null;
  error: string;
}

/**
 * Write a follow-up's steps, and its trigger or stop rule when one of those is
 * what changed. All of it goes through the automations API the builder itself
 * uses, so a follow-up edited on the board and one edited in Automations are the
 * same row, written the same way.
 */
export async function writeFollowUp(
  api: FollowUpApi,
  automationId: string,
  editor: Editor,
  saved: Editor | null,
  /** True on the way out: the browser is asked to send it anyway. */
  leaving = false,
): Promise<FollowUpWrite> {
  try {
    const init: RequestInit = leaving ? { keepalive: true } : {};
    const triggerChanged =
      !!saved && JSON.stringify(saved.trigger) !== JSON.stringify(editor.trigger);
    const stopChanged =
      !!saved && JSON.stringify(saved.stopWhen ?? null) !== JSON.stringify(editor.stopWhen ?? null);
    await api.saveGraph(automationId, { graph: treeToGraph(editor.steps) }, init);
    const detail =
      triggerChanged || stopChanged
        ? await api.update(
            automationId,
            {
              ...(triggerChanged ? { trigger: editor.trigger } : {}),
              ...(stopChanged ? { stop_when: editor.stopWhen } : {}),
            },
            init,
          )
        : await api.get(automationId);
    return { detail, error: "" };
  } catch (error) {
    return {
      detail: null,
      error: error instanceof ApiError ? error.message : "That follow-up did not save.",
    };
  }
}
